/**
 * Helpers for detecting whether a regime has been synchronised with an asset register.
 *
 * setAssetRegistriesForRegime writes `assetRegisters` at the Tree document level. The
 * in-memory regime root built by create-dynamic-tree-retriever exposes that document
 * as `node.$`. The asset-mapping flow additionally stamps `register` onto facility
 * (and deeper) nodes, but those descendant fields are also re-stamped onto the cloned
 * payloads of any folder copied OUT of a synced regime, which the FIQ-414 customer
 * report flagged: a brand-new destination regime that never went through the sync
 * action would inherit `register` on its descendants via copyReferencesToGroup and
 * appear synced if detection recursed.
 *
 * Detection therefore inspects the passed node directly (no recursion), but covers
 * both the document-level (`node.$`) and node-direct shapes so legitimately-synced
 * regimes are still detected.
 */

export function hasRegisterLink(node) {
    return (
        !!node?.$?.assetRegisters?.length ||
        !!node?.$?.register ||
        !!node?.data?.assetRegisters?.length ||
        !!node?.data?.register ||
        !!node?.assetRegisters?.length ||
        !!node?.register
    )
}

// Intentionally non-recursive — inspects the passed node only, not its descendants.
// The recursive form would over-report "synced" after copying a folder in from a
// register-synced regime, because the copied descendant payloads still carry the
// source's register field even though the destination root was never synced. See FIQ-414.
export function isSyncedToRegister(node) {
    return hasRegisterLink(node)
}
